// Small floating context menu shown on right-click.  Replaces the
// "right-click immediately spawns a new window" behavior with the
// standard browser-like "Open / Open in new window" menu.
//
// Usage:
//   const [menu, setMenu] = useState(null)
//   ...
//   <a onContextMenu={(e) => {
//     e.preventDefault()
//     setMenu({ x: e.clientX, y: e.clientY,
//       items: [
//         { label: 'Open',                 onClick: () => navigate(route) },
//         { label: 'Open in new window',   onClick: () => spawn(route) },
//       ]
//     })
//   }}>Link</a>
//   <ContextMenu menu={menu} onClose={() => setMenu(null)} />
//
// Closes on outside click, Esc, or any item selection.  Smart-positions
// to stay within the viewport.

import { useEffect, useRef, useState } from 'react'

export default function ContextMenu({ menu, onClose }) {
  const ref = useRef(null)
  const [pos, setPos] = useState({ left: -9999, top: -9999 })

  // After render, measure the menu and reposition so it stays on-screen
  useEffect(() => {
    if (!menu) return
    const el = ref.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    let left = menu.x
    let top = menu.y
    const margin = 8
    if (left + rect.width > window.innerWidth - margin)  left = window.innerWidth - rect.width - margin
    if (top + rect.height > window.innerHeight - margin) top = window.innerHeight - rect.height - margin
    if (left < margin) left = margin
    if (top < margin)  top = margin
    setPos({ left, top })
  }, [menu])

  // Outside click + Esc closes. We DELAY attaching the listeners until the
  // NEXT event loop tick so the right-click event that opened the menu
  // doesn't immediately close it (Steve 2026-06-29 — React 18's batched
  // state updates can race with the listener attachment, depending on
  // platform). Also: dropped the `contextmenu` close listener — mousedown
  // is sufficient and removes the race entirely.
  useEffect(() => {
    if (!menu) return
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose()
    }
    // preventDefault: this Esc closes the menu only (Calendar's "Esc leaves
    // full screen" checks it).
    const onKey = (e) => { if (e.key === 'Escape') { e.preventDefault(); onClose() } }
    let attached = false
    const attach = () => {
      window.addEventListener('mousedown', onDown)
      window.addEventListener('keydown', onKey)
      attached = true
    }
    // setTimeout(0) defers until after the current event loop tick — i.e.
    // after the right-click event that opened the menu has fully settled
    const t = setTimeout(attach, 0)
    return () => {
      clearTimeout(t)
      if (attached) {
        window.removeEventListener('mousedown', onDown)
        window.removeEventListener('keydown', onKey)
      }
    }
  }, [menu, onClose])

  if (!menu) return null

  return (
    <div
      ref={ref}
      role="menu"
      style={{ position: 'fixed', left: pos.left, top: pos.top, zIndex: 9999 }}
      className="bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[180px] text-sm"
    >
      {menu.items.map((item, i) => (
        item.divider ? (
          <div key={`div-${i}`} className="border-t border-gray-100 my-1" />
        ) : (
          <button
            key={item.label}
            role="menuitem"
            onClick={() => { item.onClick(); onClose() }}
            disabled={item.disabled}
            className="w-full text-left px-4 py-1.5 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {item.icon && <span className="text-gray-400">{item.icon}</span>}
            {item.label}
          </button>
        )
      ))}
    </div>
  )
}
